import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { connect } from "react-redux";

import { ReactComponent as ShopifyLogo } from "../../../assets/images/shopifyLogo.svg";
import {
  buttonNameConstants,
  shopifyConstants,
  systemConstants,
  toastMessages,
  toastType,
} from "../../../constants";
import {
  authenticationActions,
  companyActions,
  storeActions,
  systemActions,
} from "../../../redux/actions";
import { storeService } from "../../../services";
import {
  Button,
  LogoutButton,
  SelectStore,
  ShopifyCustomerSyncing,
  Topbar,
} from "../../../shared/components";
import {
  customToast,
  getShopifyConnectionLabel,
  isAdminLogin,
  isShopifyConnected,
  isSpinnerEnabled,
  isShopifyConnectionButtonDisabled,
  handleCreateSelectOptions,
  isReinstallShopifyApp,
} from "../../../shared/utility";
import { getItemFromStorage, removeItemFromStorage } from "../../../system";

const ShopifyConnection = (props) => {
  const {
    user,
    company,
    getStores,
    spinnerArray,
    currentStore,
    companyLogout,
    activateSpinner,
    deactivateSpinner,
    isActualUser = false,
    getCurrentStoreCompanyDetails,
  } = props;
  const [selectedStore, setSelectedStore] = useState(currentStore);
  const shopifyAppInstallStoreMiscDataId = getItemFromStorage(
    systemConstants.SHOPIFY_APP_INSTALL_STORE_MISC_DATA_ID
  );
  const isDummyCompany = company.id && company.isDummyCompany;
  const isConnected =
    isShopifyConnected(selectedStore) && !shopifyAppInstallStoreMiscDataId;
  const isConnecting = isSpinnerEnabled(
    spinnerArray,
    shopifyConstants.SET_SHOPIFY_CONNECTION
  );

  const handleConnectShopify = async (selectedStoreProp) => {
    try {
      activateSpinner(shopifyConstants.SET_SHOPIFY_CONNECTION);
      if (isAdminLogin(user)) {
        customToast(
          "Admin cannot install the app. You must login with company or store to install the shopify app.",
          toastType.ERROR
        );
        return;
      } else if (!selectedStoreProp && company.id && !company.isDummyCompany) {
        customToast("You must select the store to connect.", toastType.ERROR);
        return;
      } else if (
        !shopifyAppInstallStoreMiscDataId &&
        selectedStoreProp.shopify &&
        selectedStoreProp.shopify.isReinstallShopifyApp
      ) {
        let res = await storeService.connectShopify({
          shop: selectedStoreProp.shopify.shop,
          storeId: selectedStoreProp.id,
        });
        if (res) {
          window.location.replace(res);
        } else {
          customToast(toastMessages.SOMETHING_WENT_WRONG, toastType.ERROR);
        }
        return;
      } else if (!shopifyAppInstallStoreMiscDataId) {
        window.location.replace(process.env.REACT_APP_SHOPIFY_APP_INSTALL_URL);
        return;
      } else if (!selectedStoreProp?.id || !shopifyAppInstallStoreMiscDataId) {
        customToast(toastMessages.SOMETHING_WENT_WRONG, toastType.ERROR);
        return;
      }
      await storeService.connectShopifyAccessTokenFromStoreMiscDataToStore({
        storeId: selectedStoreProp?.id,
        shopifyAppInstallStoreMiscDataId: shopifyAppInstallStoreMiscDataId,
      });
      props.getUpdatedCurrentStoreDetails(selectedStoreProp?.id, (data) => {
        setSelectedStore(data);
      });
      removeItemFromStorage(
        systemConstants.SHOPIFY_APP_INSTALL_STORE_MISC_DATA_ID
      );
    } catch (error) {
      customToast(error, toastType.ERROR);
    } finally {
      deactivateSpinner(shopifyConstants.SET_SHOPIFY_CONNECTION);
    }
  };

  useEffect(() => {
    if (!currentStore) {
      getStores(user);
    } else if (currentStore?.id) {
      props.getUpdatedCurrentStoreDetails(currentStore?.id);
    }
  }, []);

  useEffect(() => {
    if (currentStore.id) {
      setSelectedStore(currentStore);
    }
    // if actual user and dummy company store means only one store then trigger the connect automatically as the user doesn't have to select the store as there is only one store to connect with shopify
    if (
      isActualUser &&
      company &&
      isDummyCompany &&
      currentStore.id &&
      shopifyAppInstallStoreMiscDataId
    ) {
      handleConnectShopify(currentStore);
    }
    if (!company.id && currentStore?.companyId) {
      getCurrentStoreCompanyDetails(currentStore.companyId);
    }
  }, [currentStore]);

  return (
    <>
      {isActualUser ? (
        <div className="position-absolute top-0 end-0 mt-2 me-2">
          <LogoutButton
            onClick={companyLogout}
            className="logout-button position-static "
          />
        </div>
      ) : (
        <Topbar title="Shopify Connection"></Topbar>
      )}
      <Container>
        <Row>
          <Col
            md={isActualUser ? 7 : 5}
            className="mx-auto d-flex flex-column gap-3 billing-card-wrapper"
          >
            <ShopifyLogo className="mx-auto" width={128} />
            {
              // As shopify user will always be dummy company that's why making sure we only show this for actual users (actual users with company, actual users with dummy company don't need to select the store)
              isActualUser && !isDummyCompany ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    <b>{company.companyName}</b> <br />
                    {isConnecting
                      ? "Connecting to Shopify..."
                      : isShopifyConnectionButtonDisabled(
                          selectedStore,
                          isConnecting,
                          isActualUser
                        )
                      ? `You're all set! This store is connected to Shopify. You can close this window to continue, or select a different store.`
                      : `Select a store to connect.`}
                  </p>
                  <SelectStore
                    isDisabled={isConnecting}
                    value={handleCreateSelectOptions(
                      selectedStore,
                      selectedStore?.storeName
                    )}
                    onChange={(e) => {
                      setSelectedStore(e.value);
                    }}
                  />{" "}
                </>
              ) : isActualUser && !isConnected ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    Welcome, <b>{company.companyName}</b>
                    <br />
                    Please click the following button to install LootLedger.
                  </p>
                </>
              ) : // if store is connected and a new app update is available then show this message for actual users
              isReinstallShopifyApp(currentStore, isActualUser, isConnected) ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    Welcome, <b>{selectedStore.storeName}</b>
                    <br />
                    Your store is connected to Shopify! An app update is now
                    available. Please click below to install the latest version.
                  </p>
                </>
              ) : // If the store is already connected then show the success message for actual users
              isActualUser && isConnected ? (
                <>
                  <p className="w-100 m-0 p-0 text-center">
                    Welcome, <b>{selectedStore.storeName}</b>
                    <br />
                    Your store has been successfully connected with Shopify! You
                    may close this window now.
                  </p>
                </>
              ) : (
                // Shopify user, don't show anything as we will render the text after button for shopify user
                <> </>
              )
            }
            <Button
              type="button"
              className={"w-100 mt-0"}
              handleClick={() => handleConnectShopify(selectedStore)}
              isDisabled={isShopifyConnectionButtonDisabled(
                selectedStore,
                isConnecting,
                isActualUser
              )}
              label={getShopifyConnectionLabel(
                selectedStore,
                isActualUser,
                isConnecting
              )}
            />
            {!isActualUser && (
              <>
                <p className="w-100 ">
                  {isConnected && (
                    <>
                      The store is connected with
                      <b> {selectedStore?.shopify?.shop} </b>
                    </>
                  )}
                  All Customers will be synced automatically, and Inventory
                  Items will need to be synced selectively.
                </p>
                {isConnected && <ShopifyCustomerSyncing />}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
  company: state.company.currentStoreCompany,
  user: state.authentication.user,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  activateSpinner: (data) => dispatch(systemActions.activateSpinner(data)),
  deactivateSpinner: (data) => dispatch(systemActions.deactivateSpinner(data)),
  getUpdatedCurrentStoreDetails: (storeId, callBack) =>
    dispatch(storeActions.getUpdatedCurrentStoreDetails(storeId, callBack)),
  getStores: (data) => dispatch(storeActions.getStores(data)),
  companyLogout: () => dispatch(authenticationActions.companyLogout()),
  getCurrentStoreCompanyDetails: (companyId) =>
    dispatch(companyActions.getCurrentStoreCompanyDetails(companyId)),
});

//-------Export AddNewUser Component
export default connect(mapStateToProps, mapDispatchToProps)(ShopifyConnection);
